import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { usePrivilegedIdleTimeout } from "@/hooks/usePrivilegedIdleTimeout";
import { supabase } from "@/integrations/supabase/client";
import BeltLoader from "@/components/BeltLoader";
import {
  type AppRole,
  LOGIN_ROUTE,
  ROUTER_ROUTE,
  SETUP_2FA_ROUTE,
  VERIFY_2FA_ROUTE,
  canAccess,
  isPrivilegedRole,
} from "@/lib/roles";

interface RouteGuardProps {
  allowedRoles: AppRole[];
  children: React.ReactNode;
}


/** Cache verified roles to avoid duplicate RPC calls during navigation */
const roleCache = new Map<string, { role: AppRole | null; expiresAt: number }>();
const ROLE_CACHE_TTL = 60_000; // 1 minute

function getCachedRole(userId: string): AppRole | null | undefined {
  const entry = roleCache.get(userId);
  if (!entry) return undefined;
  if (Date.now() > entry.expiresAt) {
    roleCache.delete(userId);
    return undefined;
  }
  return entry.role;
}

function setCachedRole(userId: string, role: AppRole | null) {
  roleCache.set(userId, { role, expiresAt: Date.now() + ROLE_CACHE_TTL });
}

export default function RouteGuard({ allowedRoles, children }: RouteGuardProps) {
  const { session, loading, getUserRole } = useAuth();
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState<boolean | null>(null);
  const [currentRole, setCurrentRole] = useState<AppRole | null>(null);
  const checkedRef = useRef<string | null>(null);

  // Onda 5 — logout por inatividade (4h) para super_admin / developer
  usePrivilegedIdleTimeout(currentRole);

  useEffect(() => {
    if (loading) return;
    if (!session) {
      navigate(LOGIN_ROUTE, { replace: true });
      return;
    }

    const userId = session.user.id;

    // Prevent duplicate checks for the same user in this mount
    if (checkedRef.current === userId) return;
    checkedRef.current = userId;

    // Try cache first
    const cached = getCachedRole(userId);
    if (cached !== undefined) {
      setCurrentRole(cached ?? null);
      if (isPrivilegedRole(cached)) {
        // força re-checagem de AAL via fluxo do servidor
      } else if (canAccess(cached, allowedRoles)) {
        setAuthorized(true);
        return;
      } else {
        navigate(ROUTER_ROUTE, { replace: true });
        return;
      }
    }

    // Fetch from server
    getUserRole(userId).then(async (role) => {
      setCachedRole(userId, role);
      setCurrentRole((role as AppRole | null) ?? null);

      // Onda 5.5 — exige AAL2 (TOTP) para super_admin / developer
      if (isPrivilegedRole(role)) {
        try {
          const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
          if (aal?.currentLevel !== "aal2") {
            const { data: factors } = await supabase.auth.mfa.listFactors();
            const verified = (factors?.totp ?? []).find((f: any) => f.status === "verified");
            navigate(verified ? VERIFY_2FA_ROUTE : SETUP_2FA_ROUTE, { replace: true });
            return;
          }
        } catch {
          // se MFA API falhar, não bloqueia — segue fluxo padrão
        }
      }

      if (canAccess(role, allowedRoles)) {
        setAuthorized(true);
      } else {
        navigate(ROUTER_ROUTE, { replace: true });
      }

    });
  }, [session, loading]);

  if (loading || authorized === null) {
    return <BeltLoader message="Verificando permissões..." />;
  }

  return <>{children}</>;
}

/** Clear the role cache (call on sign out) */
export function clearRoleCache() {
  roleCache.clear();
}
